import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsStringOrNull, toEnumFromStringOrNullOrUndefined } from '../../serialization/enum-utils.js';
import { cloneDataOrNullOrUndefined, cloneListOrNullOrUndefined, DataModel, DataModelBuilder, isExactSameList, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { LocalizedFileReference } from './localized-file-reference.js';
import { LocalizedString } from './localized-string.js';
import { Consent } from './consent.js';

export enum PoliciesAgeRestriction {
  None = 'None',
  Age16 = 'Age16',
  Age18 = 'Age18',
}

export interface PoliciesSettingsEventOverride {
  eventId: string,
  override: PoliciesSettings,
}

export interface PoliciesSettingsWithEventOverrides {
  settings: PoliciesSettings,
  eventOverrides?: PoliciesSettingsEventOverride[],
}

export class PoliciesSettings extends DataModel {
  static readonly TYPE_ID: string = 'b0afd46137124f50b2eb0fccceba0444';

  private constructor(
    public readonly termsUrl: LocalizedString | null | undefined,
    public readonly privacyPolicyUrl: LocalizedString | null | undefined,
    public readonly cookiePolicyUrl: LocalizedString | null | undefined,
    public readonly marketingConsent: Consent | null | undefined,
    public readonly ageRestriction: PoliciesAgeRestriction | null | undefined,
    public readonly parentalConsent: LocalizedFileReference | null | undefined,
    public readonly checkConsents: Consent[] | null | undefined,
  ) {
    super();
  }

  get $typeId(): string {
    return PoliciesSettings.TYPE_ID;
  }

  static create(args: {
    termsUrl?: LocalizedString | null;
    privacyPolicyUrl?: LocalizedString | null;
    cookiePolicyUrl?: LocalizedString | null;
    marketingConsent?: Consent | null;
    ageRestriction?: PoliciesAgeRestriction | null;
    parentalConsent?: LocalizedFileReference | null;
    checkConsents?: Consent[] | null;
  }): PoliciesSettings {
    return new PoliciesSettings(
        args.termsUrl,
        args.privacyPolicyUrl,
        args.cookiePolicyUrl,
        args.marketingConsent,
        args.ageRestriction,
        args.parentalConsent,
        args.checkConsents,
    );
  }

  static empty(): PoliciesSettings {
    return new PoliciesSettings(
        LocalizedString.empty(),
        LocalizedString.empty(),
        LocalizedString.empty(),
        Consent.empty(),
        PoliciesAgeRestriction.None,
        LocalizedFileReference.empty(),
        new Array<Consent>(),
    );
  }

  static emptyWithEventOverrides(): PoliciesSettingsWithEventOverrides {
    return { settings: PoliciesSettings.empty() };
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): PoliciesSettings {
    return new PoliciesSettings(
        context.fromDataOrNullOrUndefined<LocalizedString>(MapUtils.getRecordOrNullOrUndefined(map, 'termsUrl')),
        context.fromDataOrNullOrUndefined<LocalizedString>(MapUtils.getRecordOrNullOrUndefined(map, 'privacyPolicyUrl')),
        context.fromDataOrNullOrUndefined<LocalizedString>(MapUtils.getRecordOrNullOrUndefined(map, 'cookiePolicyUrl')),
        context.fromDataOrNullOrUndefined<Consent>(MapUtils.getRecordOrNullOrUndefined(map, 'marketingConsent')),
        toEnumFromStringOrNullOrUndefined(MapUtils.getStringOrNullOrUndefined(map, 'ageRestriction'), PoliciesAgeRestriction),
        context.fromDataOrNullOrUndefined<LocalizedFileReference>(MapUtils.getRecordOrNullOrUndefined(map, 'parentalConsent')),
        MapUtils.getListOrNullOrUndefined<Consent>(map, 'checkConsents', (value) => {
          return context.fromData<Consent>(value as SerializedData);
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    const result: SerializedData = {};

    if(this.termsUrl !== undefined) {
      result['termsUrl'] = context.toDataOrNull(this.termsUrl);
    }

    if(this.privacyPolicyUrl !== undefined) {
      result['privacyPolicyUrl'] = context.toDataOrNull(this.privacyPolicyUrl);
    }

    if(this.cookiePolicyUrl !== undefined) {
      result['cookiePolicyUrl'] = context.toDataOrNull(this.cookiePolicyUrl);
    }

    if(this.marketingConsent !== undefined) {
      result['marketingConsent'] = context.toDataOrNull(this.marketingConsent);
    }

    if(this.ageRestriction !== undefined) {
      result['ageRestriction'] = fromEnumAsStringOrNull(this.ageRestriction);
    }

    if(this.parentalConsent !== undefined) {
      result['parentalConsent'] = context.toDataOrNull(this.parentalConsent);
    }

    if(this.checkConsents !== undefined) {
      result['checkConsents'] = this.checkConsents?.map((e) => context.toData(e)) ?? null;
    }

    return result;
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    termsUrl = this.termsUrl,
    privacyPolicyUrl = this.privacyPolicyUrl,
    cookiePolicyUrl = this.cookiePolicyUrl,
    marketingConsent = this.marketingConsent,
    ageRestriction = this.ageRestriction,
    parentalConsent = this.parentalConsent,
    checkConsents = this.checkConsents,
  }: {
    termsUrl?: LocalizedString | null,
    privacyPolicyUrl?: LocalizedString | null,
    cookiePolicyUrl?: LocalizedString | null,
    marketingConsent?: Consent | null,
    ageRestriction?: PoliciesAgeRestriction | null,
    parentalConsent?: LocalizedFileReference | null,
    checkConsents?: Consent[] | null,
  } = {}): this {
    return new PoliciesSettings(
        termsUrl?.clone(),
        privacyPolicyUrl?.clone(),
        cookiePolicyUrl?.clone(),
        marketingConsent?.clone(),
        ageRestriction,
        parentalConsent?.clone(),
        cloneListOrNullOrUndefined<Consent>(checkConsents),
    ) as this;
  }

  mergeWith(other: PoliciesSettings | null | undefined): PoliciesSettings | null {
    if(other === undefined) {
      return this;
    }

    if(other === null) {
      return other;
    }

    return new PoliciesSettings(
      other.termsUrl !== undefined ? cloneDataOrNullOrUndefined(other.termsUrl) : cloneDataOrNullOrUndefined(this.termsUrl),
      other.privacyPolicyUrl !== undefined ? cloneDataOrNullOrUndefined(other.privacyPolicyUrl) : cloneDataOrNullOrUndefined(this.privacyPolicyUrl),
      other.cookiePolicyUrl !== undefined ? cloneDataOrNullOrUndefined(other.cookiePolicyUrl) : cloneDataOrNullOrUndefined(this.cookiePolicyUrl),
      other.marketingConsent !== undefined ? cloneDataOrNullOrUndefined(other.marketingConsent) : cloneDataOrNullOrUndefined(this.marketingConsent),
      other.ageRestriction !== undefined ? other.ageRestriction : this.ageRestriction,
      other.parentalConsent !== undefined ? cloneDataOrNullOrUndefined(other.parentalConsent) : cloneDataOrNullOrUndefined(this.parentalConsent),
      other.checkConsents !== undefined ? cloneListOrNullOrUndefined(other.checkConsents) : cloneListOrNullOrUndefined(this.checkConsents),
    );
  }

  isSame(other: PoliciesSettings): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.termsUrl, this.termsUrl) &&
      isSameData(other.privacyPolicyUrl, this.privacyPolicyUrl) &&
      isSameData(other.cookiePolicyUrl, this.cookiePolicyUrl) &&
      isSameData(other.marketingConsent, this.marketingConsent) &&
      other.ageRestriction === this.ageRestriction &&
      isSameData(other.parentalConsent, this.parentalConsent) &&
      isExactSameList<Consent>(other.checkConsents, this.checkConsents, isSameData);
  }
}

export class PoliciesSettingsBuilder extends DataModelBuilder<PoliciesSettings> {
  build(context: SerializationContext, map: SerializedData): PoliciesSettings {
    return PoliciesSettings.fromMap(context, map);
  }
}
